import React, { useState, useEffect } from 'react';
import { Select, message } from 'antd';
import { LabelValue } from 'src/library/types';
import { getVendorList } from 'src/components/pages/AssignedDrivers/AssignedDrivers.api';
import Loader from 'src/common/Loader';

interface VendorCodeFilterProps {
  value?: LabelValue | LabelValue[];
  onChange?: (value: LabelValue | LabelValue[]) => void;
  placeholder?: string;
  mode?: 'multiple' | 'single';
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  allowClear?: boolean;
  maxTagCount?: number;
  disabled?: boolean;
}

interface Vendor {
  id: string;
  code: string;
  name: string;
}

const VendorCodeFilter: React.FC<VendorCodeFilterProps> = ({
  value,
  onChange,
  placeholder = 'Vendor Code',
  mode = 'multiple',
  size = 'small',
  style = { minWidth: 150 },
  className,
  allowClear = true,
  maxTagCount = 1,
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<LabelValue[]>([]);

  const fetchAllVendors = async () => {
    setLoading(true);
    const response = await getVendorList();
    if (response.isSuccess) {
      const options: LabelValue[] = response.data?.map((vendor: Vendor) => ({
        label: vendor.code,
        value: vendor.id,
      })) || [];
      setVendors(options);
    } else {
      message.error(response.errorMessage || 'Failed to fetch vendors');
      setVendors([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllVendors();
  }, []);

  const filterVendorOptions = (input: string, option: any) => {
    const label = option?.label as string;
    return label?.toLowerCase()?.includes(input.toLowerCase()) ?? false;
  };

  const dropdownRender = (menu: React.ReactElement) => {
    if (loading) {
      return (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Loader />
        </div>
      );
    }
    return menu;
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      mode={mode === 'multiple' ? 'tags' : undefined}
      size={size}
      style={{ ...style }}
      className={className}
      allowClear={allowClear}
      maxTagCount={maxTagCount}
      disabled={disabled}
      showSearch
      showArrow
      labelInValue
      options={vendors}
      filterOption={filterVendorOptions}
      dropdownRender={dropdownRender}
      notFoundContent={loading ? null : 'No vendors found'}
    />
  );
};

export default VendorCodeFilter;
